import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';
import {
  buildQuoteEmailSnapshot,
  createQuoteAccessToken,
  sendQuoteEstablishedEmail,
} from '../../../utils/sendQuoteEmail';
import {
  formatCivilityLabel,
  getCustomerKindLabel,
  resolveCustomerKind,
  resolveSuggestedVatRate,
} from '../../../utils/quoteCustomer';
import { QUOTE_REFUSAL_REASONS } from '../../public-service-tracking/components/QuoteRefuseModal';

const formatPrice = (price) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(Number(price) || 0);

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const getRefusalReasonLabel = (reason) => {
  if (!reason) return null;
  return (
    QUOTE_REFUSAL_REASONS.find((r) => r.value === reason)?.label || reason
  );
};

const QuoteModal = ({ isOpen, onClose, intervention, onQuoteCreated }) => {
  const [loading, setLoading] = useState(false);
  const [fetchingQuote, setFetchingQuote] = useState(false);
  const [existingQuote, setExistingQuote] = useState(null);
  const [emailStatus, setEmailStatus] = useState('');
  const [prestationPrice, setPrestationPrice] = useState(0);
  const [quoteDetails, setQuoteDetails] = useState({
    reference: '',
    validUntil: '',
    notes: '',
    discountType: 'percentage',
    discountValue: 0,
    taxRate: 20,
  });

  const customerKind = useMemo(
    () => resolveCustomerKind(intervention),
    [intervention]
  );
  const suggestedVat = useMemo(
    () => resolveSuggestedVatRate(intervention),
    [intervention]
  );

  const quoteStatus = existingQuote?.status || null;
  const hasExistingQuote = Boolean(existingQuote?.id);
  const isAccepted = quoteStatus === 'accepted';
  const isRejected = quoteStatus === 'rejected' || quoteStatus === 'refused';
  const showCreateForm = !fetchingQuote && !hasExistingQuote;

  useEffect(() => {
    if (!isOpen || !intervention?.id) {
      setExistingQuote(null);
      return;
    }

    let cancelled = false;

    const loadExistingQuote = async () => {
      setFetchingQuote(true);
      setEmailStatus('');
      setPrestationPrice(Number(intervention?.price) || 0);
      setQuoteDetails((prev) => ({
        ...prev,
        reference: generateQuoteReference(),
        validUntil: getDefaultValidUntil(),
        notes: intervention.notes || '',
        taxRate: resolveSuggestedVatRate(intervention),
        discountValue: 0,
      }));

      try {
        const { data, error } = await supabase
          .from('appointment_quote')
          .select(
            'id,status,reference,refusal_reason,refusal_comment,refused_at,total_ttc,total_ht,price,valid_until,notes,created_at,updated_at'
          )
          .eq('appointment_id', intervention.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (cancelled) return;
        if (error) throw error;
        setExistingQuote(data || null);
      } catch (err) {
        console.error('Erreur chargement devis:', err);
        if (!cancelled) {
          setExistingQuote(intervention?.latest_quote || null);
        }
      } finally {
        if (!cancelled) setFetchingQuote(false);
      }
    };

    loadExistingQuote();
    return () => {
      cancelled = true;
    };
  }, [isOpen, intervention]);

  const generateQuoteReference = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `DEV-${year}${month}-${random}`;
  };

  const getDefaultValidUntil = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  const calculateDiscount = (subtotal) => {
    if (!quoteDetails.discountValue) return 0;
    if (quoteDetails.discountType === 'percentage') {
      return subtotal * (quoteDetails.discountValue / 100);
    }
    return quoteDetails.discountValue;
  };

  const calculateTotalHT = () => {
    const discount = calculateDiscount(prestationPrice);
    return prestationPrice - discount;
  };

  const calculateVAT = () => {
    const totalHT = calculateTotalHT();
    return totalHT * (quoteDetails.taxRate / 100);
  };

  const calculateTotalTTC = () => {
    return calculateTotalHT() + calculateVAT();
  };

  const buildAppointmentForPdf = () => ({
    id: intervention.id,
    reference: intervention.reference,
    client: intervention.client,
    customer_name: intervention.customer_name,
    customerName: intervention.customer_name,
    customer_email: intervention.customer_email,
    customerEmail: intervention.customer_email,
    customer_phone: intervention.customer_phone,
    customerPhone: intervention.customer_phone,
    customer_address: intervention.customer_address,
    customerAddress: intervention.customer_address,
    notes: intervention.notes,
    created_at: intervention.created_at,
    createdAt: intervention.created_at,
    serviceType: intervention.service_type || intervention.service_id?.name,
    service_type: intervention.service_type,
    problemName: intervention.problem_name || intervention.service_id?.problem?.name,
    service: intervention.service_id,
    Service: intervention.service_id,
    siret: intervention.siret,
    tva: quoteDetails.taxRate,
  });

  const handleSubmit = async () => {
    if (prestationPrice <= 0) {
      alert('Veuillez saisir un prix valide');
      return;
    }
    if (!intervention?.customer_email) {
      alert('Adresse email du contact manquante — impossible d’envoyer le devis.');
      return;
    }

    setLoading(true);
    setEmailStatus('');
    try {
      const totalHT = calculateTotalHT();
      const totalTTC = calculateTotalTTC();

      const quoteData = {
        appointment_id: intervention.id,
        reference: quoteDetails.reference,
        valid_until: quoteDetails.validUntil,
        notes: quoteDetails.notes,
        price: prestationPrice,
        discount_type: quoteDetails.discountType,
        discount_value: quoteDetails.discountValue,
        total_ht: totalHT,
        vat_rate: Number(quoteDetails.taxRate) || suggestedVat,
        total_ttc: totalTTC,
        status: 'sent',
      };

      const { data: createdQuote, error: insertError } = await supabase
        .from('appointment_quote')
        .insert(quoteData)
        .select('*')
        .single();

      if (insertError) throw insertError;

      const appointmentPayload = buildAppointmentForPdf();
      let emailErrorMessage = '';

      try {
        setEmailStatus('Envoi de l’email en cours…');
        const appointmentForEmail = {
          ...appointmentPayload,
          problem_name: appointmentPayload.problemName,
        };
        const token = createQuoteAccessToken({
          quoteId: createdQuote.id,
          appointmentId: intervention.id,
          email: intervention.customer_email,
          clientId: intervention.client || null,
          validUntil: createdQuote.valid_until,
          snapshot: buildQuoteEmailSnapshot(appointmentForEmail, createdQuote),
        });
        const acceptUrl = `${window.location.origin}/devis?token=${encodeURIComponent(token)}`;

        await sendQuoteEstablishedEmail({
          quote: createdQuote,
          appointment: appointmentForEmail,
          acceptUrl,
          includePdf: true,
        });
        setEmailStatus('Email envoyé avec le PDF en pièce jointe.');
      } catch (emailErr) {
        console.error('Erreur envoi email devis:', emailErr);
        emailErrorMessage =
          emailErr?.message || 'Le devis a été créé mais l’email n’a pas pu être envoyé.';
        setEmailStatus(emailErrorMessage);
      }

      if (onQuoteCreated) {
        onQuoteCreated(createdQuote || quoteData);
      }

      if (emailErrorMessage) {
        alert(`Devis créé. Attention : ${emailErrorMessage}`);
      }

      onClose();
    } catch (error) {
      console.error('Erreur lors de la création du devis:', error);
      alert(`Erreur lors de la création du devis: ${error.message || 'erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const discount = calculateDiscount(prestationPrice);
  const totalHT = calculateTotalHT();
  const vat = calculateVAT();
  const totalTTC = calculateTotalTTC();
  const refusalLabel = getRefusalReasonLabel(existingQuote?.refusal_reason);

  const modalTitle = fetchingQuote
    ? 'Devis'
    : !hasExistingQuote
      ? 'Établir un devis'
      : isAccepted
        ? 'Réponse client : Oui'
        : isRejected
          ? 'Réponse client : Non'
          : 'Devis envoyé';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      <div className="relative h-[107vh] flex items-center justify-center p-4">
        <div
          className={`relative bg-white rounded-xl shadow-xl w-full max-h-[90vh] overflow-hidden ${
            showCreateForm ? 'max-w-4xl' : 'max-w-lg'
          }`}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{modalTitle}</h2>
              <p className="text-sm text-gray-500 mt-1">
                Pour l&apos;intervention {intervention?.reference} —{' '}
                {intervention?.customer_name}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Icon name="X" size={20} className="text-gray-500" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {fetchingQuote || loading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <div className="flex items-center">
                  <Icon name="Loader" size={32} className="animate-spin text-primary" />
                  <span className="ml-2 text-gray-600">
                    {loading ? 'Traitement en cours…' : 'Chargement…'}
                  </span>
                </div>
                {emailStatus ? (
                  <p className="text-sm text-muted-foreground">{emailStatus}</p>
                ) : null}
              </div>
            ) : hasExistingQuote ? (
              <div className="space-y-5">
                <div
                  className={`rounded-lg border p-4 ${
                    isAccepted
                      ? 'bg-green-50 border-green-200'
                      : isRejected
                        ? 'bg-red-50 border-red-200'
                        : 'bg-amber-50 border-amber-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Icon
                      name={
                        isAccepted
                          ? 'CheckCircle'
                          : isRejected
                            ? 'XCircle'
                            : 'Clock'
                      }
                      size={28}
                      className={
                        isAccepted
                          ? 'text-green-600'
                          : isRejected
                            ? 'text-red-600'
                            : 'text-amber-600'
                      }
                    />
                    <div>
                      <p
                        className={`text-lg font-semibold ${
                          isAccepted
                            ? 'text-green-900'
                            : isRejected
                              ? 'text-red-900'
                              : 'text-amber-900'
                        }`}
                      >
                        {isAccepted
                          ? 'Oui — devis accepté'
                          : isRejected
                            ? 'Non — devis refusé'
                            : 'En attente de réponse client'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        Référence devis :{' '}
                        <strong>{existingQuote.reference || '—'}</strong>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Montant TTC</span>
                    <p className="font-medium text-gray-900">
                      {formatPrice(
                        existingQuote.total_ttc ?? existingQuote.price
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Validité</span>
                    <p className="font-medium text-gray-900">
                      {formatDate(existingQuote.valid_until)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Créé le</span>
                    <p className="font-medium text-gray-900">
                      {formatDate(existingQuote.created_at)}
                    </p>
                  </div>
                  {isRejected && existingQuote.refused_at ? (
                    <div>
                      <span className="text-gray-500">Refusé le</span>
                      <p className="font-medium text-gray-900">
                        {formatDate(existingQuote.refused_at)}
                      </p>
                    </div>
                  ) : null}
                </div>

                {isRejected ? (
                  <div className="rounded-lg border border-red-200 bg-white p-4 space-y-3">
                    <h3 className="text-sm font-semibold text-red-900 flex items-center gap-2">
                      <Icon name="MessageSquare" size={16} />
                      Motif du refus
                    </h3>
                    <p className="text-sm text-gray-900">
                      {refusalLabel || 'Motif non renseigné'}
                    </p>
                    {existingQuote.refusal_comment ? (
                      <div>
                        <span className="text-xs text-gray-500 uppercase tracking-wide">
                          Commentaire
                        </span>
                        <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">
                          {existingQuote.refusal_comment}
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {isAccepted ? (
                  <p className="text-sm text-green-800">
                    Le client a accepté ce devis.
                  </p>
                ) : null}

                {!isAccepted && !isRejected ? (
                  <p className="text-sm text-amber-900">
                    Le devis a été envoyé. Le client n&apos;a pas encore répondu.
                  </p>
                ) : null}
              </div>
            ) : (
              <>
                <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center">
                    <Icon name="User" size={16} className="mr-1" />
                    Contact
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Civilité / nom</span>
                      <p className="font-medium text-gray-900">
                        {formatCivilityLabel(intervention?.customer_name)}{' '}
                        {intervention?.customer_name || '—'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Email</span>
                      <p className="font-medium text-gray-900 break-all">
                        {intervention?.customer_email || 'Non renseigné'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Téléphone</span>
                      <p className="font-medium text-gray-900">
                        {intervention?.customer_phone || 'Non renseigné'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Adresse</span>
                      <p className="font-medium text-gray-900">
                        {intervention?.customer_address || 'Non renseignée'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-amber-900 mb-2 flex items-center">
                    <Icon name="Percent" size={16} className="mr-1" />
                    Régime TVA
                  </h3>
                  <p className="text-sm text-amber-900">
                    Type de client :{' '}
                    <strong>{getCustomerKindLabel(customerKind)}</strong>
                    {intervention?.siret ? ` — SIRET ${intervention.siret}` : ''}
                  </p>
                  <p className="text-xs text-amber-800 mt-1">
                    TVA conseillée : {suggestedVat}% (
                    {customerKind === 'business'
                      ? 'personne morale'
                      : 'personne physique'}
                    ). Vous pouvez ajuster le taux ci-dessous si besoin.
                  </p>
                </div>

                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-blue-800 mb-3 flex items-center">
                    <Icon name="Info" size={16} className="mr-1" />
                    Détails de l&apos;intervention
                  </h3>

                  <div className="space-y-2">
                    <div className="flex items-start space-x-2 text-sm">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        Catégorie :
                      </span>
                      <span className="text-gray-900">
                        {intervention?.service_id?.problem?.equipment?.category
                          ?.name || 'Non spécifiée'}
                      </span>
                    </div>
                    <div className="flex items-start space-x-2 text-sm">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        Équipement :
                      </span>
                      <span className="text-gray-900">
                        {intervention?.service_id?.problem?.equipment?.name ||
                          'Non spécifié'}
                      </span>
                    </div>
                    <div className="flex items-start space-x-2 text-sm">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        Problème :
                      </span>
                      <span className="text-gray-900">
                        {intervention?.service_id?.problem?.name ||
                          intervention?.problem_name ||
                          'Non spécifié'}
                      </span>
                    </div>
                    <div className="flex items-start space-x-2 text-sm">
                      <span className="font-medium text-gray-700 min-w-[100px]">
                        Service :
                      </span>
                      <span className="text-gray-900">
                        {intervention?.service_id?.name ||
                          intervention?.service_type ||
                          'Non spécifié'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Référence devis"
                    value={quoteDetails.reference}
                    onChange={(e) =>
                      setQuoteDetails((prev) => ({
                        ...prev,
                        reference: e.target.value,
                      }))
                    }
                  />
                  <Input
                    label="Valable jusqu'au"
                    type="date"
                    value={quoteDetails.validUntil}
                    onChange={(e) =>
                      setQuoteDetails((prev) => ({
                        ...prev,
                        validUntil: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="mb-6">
                  <Input
                    label="Prix prestation HT (€)"
                    type="number"
                    min="0"
                    step="0.01"
                    value={prestationPrice}
                    onChange={(e) =>
                      setPrestationPrice(parseFloat(e.target.value) || 0)
                    }
                  />
                </div>

                <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type de remise
                    </label>
                    <select
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      value={quoteDetails.discountType}
                      onChange={(e) =>
                        setQuoteDetails((prev) => ({
                          ...prev,
                          discountType: e.target.value,
                        }))
                      }
                    >
                      <option value="percentage">Pourcentage (%)</option>
                      <option value="fixed">Montant fixe (€)</option>
                    </select>
                  </div>
                  <Input
                    label="Valeur remise"
                    type="number"
                    min="0"
                    step="0.01"
                    value={quoteDetails.discountValue}
                    onChange={(e) =>
                      setQuoteDetails((prev) => ({
                        ...prev,
                        discountValue: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                  <Input
                    label="TVA (%)"
                    type="number"
                    min="0"
                    step="0.1"
                    value={quoteDetails.taxRate}
                    onChange={(e) =>
                      setQuoteDetails((prev) => ({
                        ...prev,
                        taxRate: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm min-h-[90px]"
                    value={quoteDetails.notes}
                    onChange={(e) =>
                      setQuoteDetails((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="mb-2 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">
                    Récapitulatif
                  </h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Prix HT</span>
                      <span>{formatPrice(prestationPrice)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Remise</span>
                      <span>- {formatPrice(discount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total HT</span>
                      <span>{formatPrice(totalHT)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        TVA ({quoteDetails.taxRate}%)
                      </span>
                      <span>{formatPrice(vat)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base pt-2 border-t border-gray-200">
                      <span>Total TTC</span>
                      <span>{formatPrice(totalTTC)}</span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mt-4">
                  À la création, un email HTML avec le PDF en pièce jointe sera
                  envoyé à{' '}
                  <strong>{intervention?.customer_email || '—'}</strong>, avec
                  un lien d&apos;acceptation (compte ou non).
                </p>
              </>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            {showCreateForm ? (
              <>
                <Button variant="outline" onClick={onClose} disabled={loading}>
                  Annuler
                </Button>
                <Button
                  variant="default"
                  onClick={handleSubmit}
                  disabled={loading || fetchingQuote}
                  iconName={loading ? 'Loader' : 'Send'}
                  iconPosition="left"
                >
                  {loading ? 'Création / envoi…' : 'Créer et envoyer le devis'}
                </Button>
              </>
            ) : (
              <Button variant="default" onClick={onClose}>
                Fermer
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteModal;
